import React, { useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { EffectCoverflow } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { ThumbnailInLoadingState } from '../../GameThumbnails/ThumbnailInLoadingState';
import { GridPageStripGamesSwiper } from '../GridPageStripGamesSwiper';
import './GridPageStripGamesDesignInteractiveGameCards.css';
import { ImageType } from '../../../../../shared/src/components/Image';
import { GamesService } from '../../../../../shared/src/services/games.service';
import { PerfUtilService } from '../../../../../shared/src/services/util/perf-util.service';

const renderPreloadThumbnails = () => {
    const gamesToRender = (new Array(20)).fill(undefined);

    return gamesToRender.map((gameId, index) => (
        <SwiperSlide key={`${gameId}-${index}`}>
            <ThumbnailInLoadingState />
        </SwiperSlide>
    ));
};

const renderGamesThumbnails = (gamesToRender, renderFn, forcedThumbSize) =>
    gamesToRender.map((gameId, index) => {
        const content = renderFn(gameId, {
            imageType: ImageType.VERTICAL_RECTANGLE,
            thumbnailSize: forcedThumbSize,
        });

        return content ? (
            <SwiperSlide key={`${gameId}-${index}`}>
                {content}
            </SwiperSlide>
        ) : null;
    });

const breakpointsConfig = {
    640: {
        coverflowEffect: {
            stretch: 20,
        }
    },
};

export const GridPageStripGamesDesignInteractiveGameCards = ({ games, renderCasinoThumbnailFn, strip }) => {
    const gamesToRender = games.slice(0, 20); // limit games number to optimize performance
    const { platformGamesReady } = useSelector((state) => state.app);
    const [forcedThumbSize, setForcedThumbSize] = useState('small');
    const itemsToRender = platformGamesReady
        ? renderGamesThumbnails(gamesToRender, renderCasinoThumbnailFn, forcedThumbSize)
        : renderPreloadThumbnails();

    /**
     * Size calculation uses actual element sizes
     * Swiper changes element's position on z-axis - so calculation mises scale multiplication
     * and results as smaller size
     *
     * More over, size change can't be detected
     * As a result - when item turns to 0 by z-axis it remains smaller thumb size
     *
     * To bypass it, let's calculate thumb size based on predefined sizes razer on actual ones.
     */
    useEffect(() => {
        const calcForcedThumbSize = () => {
            const { innerWidth } = window;
            // As per CSS
            let width = 114;
            let height = 140;
            if (innerWidth >= 1920) {
                width = 249;
                height = 322;
            } else if (innerWidth >= 1440) {
                width = 193;
                height = 238;
            } else if (innerWidth >= 1024) {
                width = 162;
                height = 200;
            }
            setForcedThumbSize(GamesService.getThumbnailSizeClass(width, height));
        };

        // initial calculation
        calcForcedThumbSize();

        // recalculate size classes on resize
        const debouncedSizeCalculation = PerfUtilService.debounce(calcForcedThumbSize, 50);
        window.addEventListener('resize', debouncedSizeCalculation);

        return () => {
            window.removeEventListener('resize', debouncedSizeCalculation);
        };
    }, []);

    const swiperAs = useCallback(({ children, modules, ...props }) => {
        const classesShadowStart = classNames(
            'grid-pages-strip-interactive-game-cards__shadow',
            'grid-pages-strip-interactive-game-cards__shadow__start'
        );
        const classesShadowEnd = classNames(
            'grid-pages-strip-interactive-game-cards__shadow',
            'grid-pages-strip-interactive-game-cards__shadow__end'
        );

        const swiperModules = [
            ...modules,
            EffectCoverflow,
        ];

        const swiperProps = {
            ...props,
            modules: swiperModules,
        };

        return (
            <div className="grid-pages-strip-interactive-game-cards__swiper">
                <div className={classesShadowStart}></div>

                <Swiper {...swiperProps}>
                    {children}
                </Swiper>

                <div className={classesShadowEnd}></div>
            </div>
        );
    }, []);

    return (
        <GridPageStripGamesSwiper
            wrapperClassName="grid-pages-strip-interactive-game-cards"
            as={swiperAs}
            spaceBetween={0}
            effect="coverflow"
            coverflowEffect={{
                depth: 200,
                rotate: 0,
                stretch: 0,
                scale: 1,
                modifier: 1,
            }}
            loop={true}
            centeredSlides={true}
            breakpoints={breakpointsConfig}
            strip={strip}
        >
            {itemsToRender}
        </GridPageStripGamesSwiper>
    );
};
